import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import { Mail, Phone, MapPin, Send } from 'lucide-react';

export const Contact = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    processNeeded: '',
    projectVolume: '',
    material: '',
    targetLeadTime: '',
    description: '',
    file: null
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'file') {
      setFormData(prev => ({ ...prev, [name]: files[0] }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Prepare form data for Web3Forms
      const web3formsData = {
        access_key: '7e11382a-24e6-48c1-a38e-48e2c969f91f',
        subject: `RFQ Request from ${formData.name}`,
        from_name: formData.name,
        name: formData.name,
        company: formData.company,
        email: formData.email,
        phone: formData.phone,
        process_needed: formData.processNeeded,
        project_volume: formData.projectVolume,
        material: formData.material,
        target_lead_time: formData.targetLeadTime,
        description: formData.description,
        message: `
RFQ REQUEST FROM WEBSITE

Name: ${formData.name}
Company: ${formData.company}
Email: ${formData.email}
Phone: ${formData.phone}
Process Needed: ${formData.processNeeded}
Project Volume: ${formData.projectVolume}
Material: ${formData.material}
Target Lead Time: ${formData.targetLeadTime}

Description:
${formData.description}
        `.trim()
      };

      // Submit to Web3Forms
      const response = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(web3formsData)
      });

      const result = await response.json();

      if (result.success) {
        toast({
          title: "RFQ Submitted Successfully!",
          description: "We'll respond within 24-48 business hours.",
        });

        // Reset form
        setFormData({
          name: '',
          company: '',
          email: '',
          phone: '',
          processNeeded: '',
          projectVolume: '',
          material: '',
          targetLeadTime: '',
          description: '',
          file: null
        });
      } else {
        throw new Error('Submission failed');
      }
      
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: "Please try again or email us directly at rfq@allpartsmanufacturing.com",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8 bg-secondary/30">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-5xl font-bold text-foreground mb-6">Start Your Next Project</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            Submit your RFQ and get a response within 24-48 business hours. Our team is ready to help you with your precision parts manufacturing needs.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6 text-center">
                <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-bold mb-2">Email RFQ</h3>
                <a href="mailto:rfq@allpartsmanufacturing.com" className="text-accent hover:underline">
                  rfq@allpartsmanufacturing.com
                </a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6 text-center">
                <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Phone className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-bold mb-2">Call Us</h3>
                <a href="tel:+16048300143" className="text-accent hover:underline">
                  +1-604-830-0143
                </a>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-smooth">
              <CardContent className="p-6 text-center">
                <div className="h-12 w-12 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <MapPin className="h-6 w-6 text-accent" />
                </div>
                <h3 className="font-bold mb-2">Location</h3>
                <p className="text-muted-foreground text-sm">Canada (Headquarters)</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* RFQ Form */}
      <section className="pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <Card>
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold mb-6">Request for Quote</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Name *</Label>
                    <Input 
                      id="name" 
                      name="name" 
                      value={formData.name}
                      onChange={handleChange}
                      required 
                      placeholder="John Doe"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="company">Company *</Label>
                    <Input 
                      id="company" 
                      name="company" 
                      value={formData.company}
                      onChange={handleChange}
                      required 
                      placeholder="Your Company"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input 
                      id="email" 
                      name="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleChange}
                      required 
                      placeholder="john@company.com"
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Phone</Label>
                    <Input 
                      id="phone" 
                      name="phone" 
                      type="tel" 
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 (604) 555-0100"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="processNeeded">Manufacturing Process Needed *</Label>
                  <Select 
                    value={formData.processNeeded}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, processNeeded: value }))}
                    required
                  >
                    <SelectTrigger className="mt-2">
                      <SelectValue placeholder="Select a process" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cnc">CNC Machining</SelectItem>
                      <SelectItem value="injection">Injection Molding</SelectItem>
                      <SelectItem value="die-casting">Die Casting</SelectItem>
                      <SelectItem value="sheet-metal">Sheet Metal</SelectItem>
                      <SelectItem value="extrusion">Aluminum Extrusion</SelectItem>
                      <SelectItem value="assembly">Assembly</SelectItem>
                      <SelectItem value="multiple">Multiple Processes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="projectVolume">Project Volume</Label>
                    <Select 
                      value={formData.projectVolume}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, projectVolume: value }))}
                    >
                      <SelectTrigger className="mt-2">
                        <SelectValue placeholder="Select volume" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="prototype">Prototype (1-50)</SelectItem>
                        <SelectItem value="low">Low Volume (50-500)</SelectItem>
                        <SelectItem value="production">Production (500+)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="material">Primary Material</Label>
                    <Input 
                      id="material" 
                      name="material" 
                      value={formData.material}
                      onChange={handleChange}
                      placeholder="e.g., 6061-T6 Aluminum"
                      className="mt-2"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="targetLeadTime">Target Lead Time</Label>
                  <Input 
                    id="targetLeadTime" 
                    name="targetLeadTime" 
                    value={formData.targetLeadTime}
                    onChange={handleChange}
                    placeholder="e.g., 4-6 weeks"
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="description">Project Description *</Label>
                  <Textarea 
                    id="description" 
                    name="description" 
                    value={formData.description}
                    onChange={handleChange}
                    required 
                    placeholder="Please describe your project requirements, critical features, and any special considerations..."
                    rows={5}
                    className="mt-2"
                  />
                </div>

                <div>
                  <Label htmlFor="file">Attach Drawings or Specifications</Label>
                  <Input 
                    id="file" 
                    name="file" 
                    type="file" 
                    onChange={handleChange}
                    accept=".pdf,.dwg,.dxf,.step,.iges,.stp"
                    className="mt-2"
                  />
                  <p className="text-xs text-muted-foreground mt-2">
                    Accepted formats: PDF, DWG, DXF, STEP, IGES (max 10MB)
                  </p>
                </div>

                <div className="pt-4">
                  <Button 
                    type="submit" 
                    size="lg" 
                    className="w-full bg-accent hover:bg-accent/90"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? 'Submitting...' : 'Submit RFQ'}
                    <Send className="ml-2 h-5 w-5" />
                  </Button>
                </div>

                <p className="text-sm text-muted-foreground text-center">
                  <strong>Response Commitment:</strong> We respond to all RFQs within 24-48 business hours
                </p>
              </form>
            </CardContent>
          </Card>

          {/* Alternative Contact */}
          <Card className="mt-8 bg-secondary/30">
            <CardContent className="p-6 text-center">
              <h3 className="font-bold mb-2">Prefer to Email Directly?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                You can also send your RFQ directly to our team
              </p>
              <a href="mailto:rfq@allpartsmanufacturing.com">
                <Button variant="outline">
                  <Mail className="mr-2 h-4 w-4" />
                  rfq@allpartsmanufacturing.com
                </Button>
              </a>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
